import React from 'react';
import { NavLink, useLocation } from 'react-router';
import { 
  LogOut, 
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { navigationConfig } from '../config/navigation';

interface AppSidebarProps {
  onLogout: () => void;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({ onLogout }) => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const { state, isMobile } = useSidebar();

  // Filter menu items based on permissions
  const menuItems = navigationConfig.filter(item => {
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }
    // Check if any sub-item is visible (if top-level item serves as a group)
    // In flattened view, we generally show the top level item if user has permission
    // But if top level item strictly relies on sub-items permission, checking item.permission is usually enough
    // as configured in navigation.tsx
    return true;
  });

  return (
    <Sidebar collapsible="icon" className="border-r-0 print:hidden">
      {!isMobile && (
        <SidebarHeader className="border-b border-green-800 bg-white h-14 flex items-center justify-center">
          {state === "expanded" && <img src="/images/logo.png" alt="El Mozuelo" className="h-12 object-contain" />}
          {state === "collapsed" && <span className="text-xl font-bold text-green-900">EM</span>}
        </SidebarHeader>
      )}

      <SidebarContent className="bg-green-900">
        {/* Main Navigation */}
        <SidebarGroup className="px-2 py-3">
          <SidebarGroupContent>
            <SidebarMenu className="gap-1.5">
              {menuItems.map((item) => {
                // Check if active: exact match or starts with path (for sub-sections)
                // Since we removed sub-menus from sidebar, we just highlight the parent module
                const isActive = location.pathname.startsWith(item.path);

                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      asChild
                      size="lg"
                      tooltip={item.label}
                      isActive={isActive}
                      className={[
                        // Base
                        "relative h-12 rounded-xl px-3 text-[0.95rem] font-medium tracking-tight",
                        "text-green-100/90 transition-all duration-150",
                        // Iconos
                        "[&>svg]:size-5 [&>svg]:shrink-0 [&>svg]:transition-transform",
                        // Hover
                        "hover:bg-green-800/70 hover:text-white hover:[&>svg]:scale-110",
                        // Active
                        "data-[active=true]:bg-green-700 data-[active=true]:text-white",
                        "data-[active=true]:[&>svg]:text-white",
                      ].join(" ")}
                    >
                      <NavLink to={item.path}>
                        {item.icon && <item.icon />}
                        <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-green-900 border-t border-green-800/60 p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={onLogout}
              size="lg"
              tooltip="Cerrar Sesión"
              className={[
                "h-12 rounded-xl px-3 text-[0.95rem] font-medium",
                "text-green-200/90 transition-all duration-150",
                "[&>svg]:size-5 [&>svg]:shrink-0 [&>svg]:transition-transform",
                "hover:bg-red-600/80 hover:text-white hover:[&>svg]:translate-x-0.5",
              ].join(" ")}
            >
              <LogOut />
              <span className="group-data-[collapsible=icon]:hidden">Cerrar Sesión</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
